import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { spring, useUITheme, ThemeToggle, pageTheme } from "@/components/ios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const { dark, toggle } = useUITheme();
  const t = pageTheme(dark);

  const inputCls = `w-full min-h-[48px] rounded-xl border border-transparent px-4 text-[16px] focus:outline-none focus:ring-2 transition-[background-color,box-shadow] duration-200 ${t.input}`;

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError(null);
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className={`min-h-screen flex items-center justify-center px-5 py-10 font-sans ${t.page}`} data-testid="reset-page" data-theme={dark ? "dark" : "light"}>
      <div className="fixed top-5 right-5 z-40">
        <ThemeToggle dark={dark} onToggle={toggle} testid="reset-theme-toggle" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={spring}
        className={`rounded-[32px] p-8 md:p-12 max-w-md w-full ${t.card}`}
      >
        <div className="mb-8"><Logo /></div>
        <h1 className={`font-bold tracking-tight text-2xl md:text-[28px] mb-7 ${t.primary}`}>Choose a new password.</h1>
        {done ? (
          <>
            <div className={`rounded-2xl border p-4 mb-7 flex items-center gap-3 ${t.noticeBox}`}>
              <CheckCircle2 className={`w-5 h-5 ${t.noticeText} shrink-0`} strokeWidth={1.5} />
              <p className={`text-[15px] font-medium ${t.noticeText}`} data-testid="reset-success">Password updated. You can sign in now.</p>
            </div>
            <Link to="/login" data-testid="reset-signin-link"
              className={`inline-flex items-center gap-2 min-h-[48px] px-6 rounded-full ${t.primaryBtn} text-[15px] font-semibold active:scale-[0.98] transition-transform duration-150`}>
              Sign in <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password (8+ characters)"
              data-testid="reset-password-input"
              className={inputCls}
            />
            <input
              type="password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              data-testid="reset-confirm-input"
              className={inputCls}
            />
            <button
              type="submit"
              disabled={loading || !token}
              data-testid="reset-submit-button"
              className={`w-full min-h-[48px] rounded-full ${t.primaryBtn} text-[15px] font-semibold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform duration-150`}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : "Update password"}
            </button>
            {!token && <p className="text-[13px] font-medium text-[#FF3B30]" data-testid="reset-no-token">This reset link is missing its token.</p>}
            {error && <p className="text-[13px] font-medium text-[#FF3B30]" data-testid="reset-error">{error}</p>}
          </form>
        )}
        <Link to="/" data-testid="reset-back-link"
          className={`inline-flex items-center gap-2 text-[14px] ${t.linkMuted} transition-colors duration-200 mt-8 min-h-[44px]`}>
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to Doclyx
        </Link>
      </motion.div>
    </main>
  );
}
